import React from "react";
import { MapPin, Clock, Phone, Mail, Calendar as CalendarIcon, CircleCheck, Loader, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const sharedData = {
  businessName: "QST Geotechnical",
  address: "https://www.qstgeo.com/",
  phone: "N/A",
  email: "info@qstgeo.com",
  hours: "N/A"
};
export default function ServiceAreaAvailability() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const displayAddress = sharedData.address.startsWith("http") ? "Serving California & Beyond" : sharedData.address;
  const displayHours = sharedData.hours === "N/A" ? "Flexible Scheduling Available" : sharedData.hours;
  const displayContact = sharedData.phone === "N/A" ? sharedData.email : sharedData.phone;
  const contactLink = sharedData.phone === "N/A" ? `mailto:${sharedData.email}` : `tel:${sharedData.phone}`;
  const ContactIcon = sharedData.phone === "N/A" ? Mail : Phone;
  return <section id="service-area-availability" ref={ref} className="dark bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1550713/img/dark_purple_map_bg.png)] bg-cover bg-center opacity-30 mix-blend-overlay pointer-events-none" />
      <div className="absolute inset-0 bg-background/85 pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4">
        <div className={`transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          
          {}
          <div className="flex flex-col items-center text-center mb-12 space-y-4">
            <Badge variant="default" className="bg-primary text-primary-foreground animate-pulse px-4 py-1 text-xs uppercase tracking-widest border-none shadow-[0_0_15px_rgba(107,40,161,0.5)]">
              Rapid Mobilization
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground">
              Service Area & Availability
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Check if we service your location and request a site visit. We prioritize rapid response for urgent geotechnical needs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {}
            <div className="lg:col-span-5 flex flex-col justify-center space-y-10 py-8">
              <div className="space-y-8">
                {}
                <div className="flex items-start space-x-4 group" data-index={0}>
                  <div className="p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <MapPin className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold uppercase tracking-wide text-foreground mb-1">Service Region</h3>
                    <p className="text-muted-foreground text-base font-medium">Serving NY, CT, PA and Beyond</p>
                    <p className="text-sm text-muted-foreground/60 mt-1">Headquarters: Hudson Valley NY</p>
                  </div>
                </div>

                {}
                <div className="flex items-start space-x-4 group" data-index={1}>
                  <div className="p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <Clock className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold uppercase tracking-wide text-foreground mb-1">Operating Hours</h3>
                    <p className="text-muted-foreground text-base font-medium">{displayHours}</p>
                    <p className="text-sm text-muted-foreground/60 mt-1">24/7 Emergency Response Available</p>
                  </div>
                </div>

                {}
                <div className="flex items-start space-x-4 group" data-index={2}>
                  <div className="p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <ContactIcon className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold uppercase tracking-wide text-foreground mb-1">Direct Line</h3>
                    <Link to={contactLink} className="text-muted-foreground text-base font-medium hover:text-primary transition-colors">
                      {displayContact}
                    </Link>
                    <p className="text-sm text-muted-foreground/60 mt-1">Reach our dispatch team directly</p>
                  </div>
                </div>
              </div>

              {}
              <div className="hidden lg:block p-6 border border-primary/20 rounded-xl bg-primary/5 backdrop-blur-sm">
                <p className="text-sm text-primary font-medium uppercase tracking-wider mb-2">Why Choose Us?</p>
                <ul className="space-y-2">
                  {["Fast Turnaround Times", "Certified Professionals", "State-of-the-Art Equipment"].map((item, i) => <li key={i} className="flex items-center text-muted-foreground text-sm" data-index={i}>
                      <CircleCheck className="size-4 mr-2 text-primary" />
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>

            {}
            <div className="lg:col-span-7">
              <Card className="border-border/50 bg-card/95 backdrop-blur-md shadow-2xl">
                <CardHeader className="pb-4 border-b border-border/50">
                  <CardTitle className="text-xl uppercase tracking-wide">Check Availability</CardTitle>
                  <CardDescription>Enter your details to verify service coverage and schedule a visit.</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <FormProvider formId="service_area_form_home" sectionName="service-area-availability" defaultValues={{
                  name: "",
                  email: "",
                  phone: "",
                  zip_code: "",
                  preferred_date: "",
                  additional_info: ""
                }} validationRules={{
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  zip_code: {
                    required: true,
                    pattern: "^[0-9]{5}(?:-[0-9]{4})?$"
                  }
                }} submitText="Check Availability" successMessage="Coverage confirmed! We will contact you shortly to finalize details." errorMessage="Something went wrong. Please try again or contact us directly.">
                    <ServiceAreaForm />
                  </FormProvider>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </div>
    </section>;
}
function ServiceAreaForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="size-16 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 mb-2">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-xl font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Check Another Location
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-input/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-input/50" />
              </FormControl>
              <FormDescription className="text-xs">We'll send confirmation here.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-input/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="zip_code" rules={{
        required: "Zip Code is required",
        pattern: {
          value: /^[0-9]{5}(?:-[0-9]{4})?$/,
          message: "Invalid Zip Code format (e.g. 90210)"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Zip Code <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="90210" {...field} className="bg-input/50" />
              </FormControl>
              <FormDescription className="text-xs">To check service coverage.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Date <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="date" {...field} className="bg-input/50 block w-full" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="mt-6">
        <FormField control={control} name="additional_info" render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Information <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Textarea placeholder="Any special requirements, site access details, or notes..." className="min-h-[100px] bg-input/50 resize-y" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      {}
      <div className="mt-8">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] rounded-full" size="lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Checking Availability...
            </> : 'Check Availability'}
        </Button>
      </div>
    </Form>;
}